import Link from "next/link";
import { ArrowUpRight, ScrollText } from "lucide-react";
import { NextSeo } from "next-seo";
import Footer from "@/components/shared/Footer";

type LegalDoc = {
  title: string;
  description: string;
  href: string;
  effectiveDate?: string;
};

const LEGAL_DOCS: LegalDoc[] = [
  {
    title: "Company Verification",
    description:
      "Legal name, date of incorporation, founders, and the official accounts of Crayo, Inc., for partners and registries to verify.",
    href: "/company-verification",
  },
  {
    title: "Terms of Service",
    description:
      "The rules that govern your use of Crayo AI, including acceptable use, intellectual property, and your rights.",
    href: "/tos",
    effectiveDate: "March 21st, 2024",
  },
  {
    title: "Privacy Policy",
    description:
      "How we collect, use, and protect your personal information when you use Crayo AI.",
    href: "/privacy-policy",
    effectiveDate: "January 24, 2025",
  },
  {
    title: "Refund Policy",
    description:
      "How refunds, cancellations, and billing work for Crayo AI subscriptions and purchases.",
    href: "/refund-policy",
    effectiveDate: "January 24, 2025",
  },
  {
    title: "Affiliate Terms of Service",
    description:
      "The terms for the Crayo AI affiliate program, including commissions, payouts, and prohibited activities.",
    href: "/affiliate-tos",
  },
];

export default function LegalHub() {
  return (
    <>
      <NextSeo
        title="Crayo AI | Legal"
        description="Legal documents for Crayo AI: terms of service, privacy policy, refund policy, and affiliate terms."
      />

      <article className="w-full overflow-x-clip bg-white">
        <section className="border-b border-[#E7ECF6] bg-white">
          <div className="mx-auto w-full max-w-screen-xl px-5 pb-12 pt-12 sm:px-6 md:px-12 md:pb-16 md:pt-16 lg:px-20">
            <div className="flex max-w-[820px] flex-col items-start gap-5">
              <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-crayoBlue">
                <ScrollText className="size-3.5" strokeWidth={2.25} />
                Legal
              </span>
              <h1 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-[56px] lg:leading-[1.05]">
                Legal documents
              </h1>
              <p className="max-w-[640px] text-[15px] leading-[1.6] text-[#5C6678] sm:text-[17px]">
                Everything that governs your use of Crayo AI: the rules, how we handle your data,
                and how billing works. Questions? Email{" "}
                <a
                  href="mailto:support@crayo.ai"
                  className="font-medium text-crayoBlue underline decoration-crayoBlue/30 underline-offset-[3px] hover:decoration-crayoBlue"
                >
                  support@crayo.ai
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto w-full max-w-screen-xl px-5 py-12 sm:px-6 md:px-12 md:py-16 lg:px-20 lg:py-20">
            <ul className="grid max-w-[1080px] grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
              {LEGAL_DOCS.map((doc) => (
                <li key={doc.href}>
                  <Link
                    href={doc.href}
                    prefetch={false}
                    className="group relative flex h-full flex-col rounded-2xl border border-[#E7ECF6] bg-white p-6 transition-all duration-200 hover:border-crayoBlue/40 hover:shadow-[0_6px_24px_-8px_rgba(77,127,237,0.22)]"
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
                        {doc.effectiveDate ? `Effective ${doc.effectiveDate}` : "Crayo AI"}
                      </span>
                      <ArrowUpRight
                        className="size-4 text-[#8B93A7] transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-crayoBlue"
                        strokeWidth={2}
                      />
                    </div>
                    <h2 className="mb-2 text-[22px] font-semibold leading-[1.2] tracking-tight text-gray-900 sm:text-[24px]">
                      {doc.title}
                    </h2>
                    <p className="text-[14px] leading-[1.6] text-[#5C6678]">{doc.description}</p>
                    <span className="mt-auto inline-flex items-center gap-1 pt-5 text-[12.5px] font-medium text-crayoBlue">
                      Read document
                      <ArrowUpRight className="size-3.5" strokeWidth={2.25} aria-hidden />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </article>

      <Footer />
    </>
  );
}
